import React, { useState, useEffect } from 'react';
import User, { USER_ROLES, USER_RESOURCES } from '../models/User';
import { Button, Input, Card, Modal, Alert } from '../components/ui';

const roleOptions = [
  { value: USER_ROLES.ADMIN, label: '管理员' },
  { value: USER_ROLES.PRACTITIONER, label: '调理师' },
  { value: USER_ROLES.RECEPTIONIST, label: '前台' },
  { value: USER_ROLES.STAFF, label: '员工' }
];

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ username: '', name: '', role: USER_ROLES.STAFF, isActive: true });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 加载用户
  useEffect(() => {
    setUsers(User.获取所有用户());
  }, []);

  // 搜索过滤
  const filteredUsers = users.filter(u =>
    u.name.includes(search) || u.username.includes(search)
  );

  // 打开新增/编辑弹窗
  const openModal = (user = null) => {
    setEditUser(user);
    setForm(user ? { ...user } : { username: '', name: '', role: USER_ROLES.STAFF, isActive: true });
    setShowModal(true);
    setError('');
  };

  // 关闭弹窗
  const closeModal = () => {
    setShowModal(false);
    setEditUser(null);
    setForm({ username: '', name: '', role: USER_ROLES.STAFF, isActive: true });
    setError('');
  };

  // 表单输入
  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // 保存用户
  const handleSave = () => {
    if (!form.username || !form.name) {
      setError('用户名和姓名不能为空');
      return;
    }
    try {
      if (editUser) {
        User.更新用户(editUser.id, form);
        setSuccess('用户信息已更新');
      } else {
        // 检查用户名唯一
        if (users.some(u => u.username === form.username)) {
          setError('用户名已存在');
          return;
        }
        User.新增用户(form);
        setSuccess('用户已添加');
      }
      setUsers(User.获取所有用户());
      closeModal();
    } catch (e) {
      setError(e.message || '操作失败');
    }
  };

  // 删除用户
  const handleDelete = id => {
    if (window.confirm('确定要删除该用户吗？')) {
      User.删除用户(id);
      setUsers(User.获取所有用户());
      setSuccess('用户已删除');
    }
  };

  // 启用/禁用用户
  const handleToggleActive = user => {
    User.更新用户(user.id, { isActive: !user.isActive });
    setUsers(User.获取所有用户());
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">员工管理</h1>
      <div className="mb-4 flex items-center space-x-2">
        <Input
          placeholder="搜索姓名或用户名"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-64"
        />
        <Button onClick={() => openModal()} variant="primary">新增员工</Button>
      </div>
      {success && <Alert type="success" onDismiss={() => setSuccess('')}>{success}</Alert>}
      <Card>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-2">姓名</th>
              <th className="px-4 py-2">用户名</th>
              <th className="px-4 py-2">角色</th>
              <th className="px-4 py-2">状态</th>
              <th className="px-4 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id} className="border-b">
                <td className="px-4 py-2">{user.name}</td>
                <td className="px-4 py-2">{user.username}</td>
                <td className="px-4 py-2">{roleOptions.find(r => r.value === user.role)?.label || user.role}</td>
                <td className="px-4 py-2">
                  <span className={user.isActive ? 'text-emerald-600' : 'text-gray-400'}>
                    {user.isActive ? '启用' : '禁用'}
                  </span>
                </td>
                <td className="px-4 py-2 space-x-2">
                  <Button size="small" onClick={() => openModal(user)}>编辑</Button>
                  <Button size="small" variant="danger" onClick={() => handleDelete(user.id)}>删除</Button>
                  <Button size="small" variant="secondary" onClick={() => handleToggleActive(user)}>
                    {user.isActive ? '禁用' : '启用'}
                  </Button>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-gray-400">暂无数据</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal open={showModal} onClose={closeModal} title={editUser ? '编辑员工' : '新增员工'}>
        <div className="space-y-4">
          {error && <Alert type="error" onDismiss={() => setError('')}>{error}</Alert>}
          <Input
            label="用户名"
            name="username"
            value={form.username}
            onChange={handleChange}
            disabled={!!editUser}
            placeholder="请输入用户名"
          />
          <Input
            label="姓名"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="请输入姓名"
          />
          <div>
            <label className="block mb-1 font-medium">角色</label>
            <select name="role" value={form.role} onChange={handleChange} className="w-full border rounded px-3 py-2">
              {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} id="isActive" />
            <label htmlFor="isActive">启用</label>
          </div>
          <div className="flex justify-end space-x-2 pt-2">
            <Button onClick={closeModal} variant="secondary">取消</Button>
            <Button onClick={handleSave} variant="primary">保存</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UsersPage; 